import { useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Zap, ArrowLeft, CheckCircle2, Clock, AlertTriangle, XCircle,
  RefreshCw, ExternalLink, Unlink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface ConnectStatus {
  status: "not_started" | "pending" | "complete" | "restricted";
  stripeConnectedAccountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsDue: string[];
}

function StatusBadge({ status }: { status: ConnectStatus["status"] }) {
  if (status === "complete") {
    return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>;
  }
  if (status === "pending") {
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Setup in progress</Badge>;
  }
  if (status === "restricted") {
    return <Badge className="bg-red-100 text-red-800 border-red-200"><AlertTriangle className="w-3 h-3 mr-1" />Action required</Badge>;
  }
  return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />Not set up</Badge>;
}

export default function AdminBillingSetup() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const search = useSearch();

  const { data: connectStatus, isLoading } = useQuery<ConnectStatus>({
    queryKey: ["/api/admin/stripe-connect/status"],
    retry: false,
  });

  const startConnect = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/stripe-connect/start"),
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url;
    },
    onError: (e: Error) => toast({ title: "Could not start payment setup", description: e.message, variant: "destructive" }),
  });

  const disconnectConnect = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/admin/stripe-connect/disconnect");
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Could not disconnect");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/stripe-connect/status"] });
      toast({ title: "Payments disconnected", description: "Your payment account has been unlinked." });
    },
    onError: (e: Error) => toast({ title: "Cannot disconnect", description: e.message, variant: "destructive" }),
  });

  const openStripeDashboard = useMutation({
    mutationFn: async (win: Window | null) => {
      const res = await apiRequest("POST", "/api/admin/stripe-connect/login-link");
      const data = await res.json();
      if (!res.ok) {
        win?.close();
        throw new Error(data.message || "Could not generate login link");
      }
      // Set the URL on the already-opened window so popup blockers don't block it.
      if (win) win.location.href = (data as { url: string }).url;
      return data as { url: string };
    },
    onError: (e: Error) => toast({ title: "Cannot open Stripe dashboard", description: e.message, variant: "destructive" }),
  });

  const refreshStatus = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/stripe-connect/refresh"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/stripe-connect/status"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/org-billing-profile"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/org-settings"] });
      toast({ title: "Status updated" });
    },
    onError: (e: Error) => toast({ title: "Could not refresh status", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    const params = new URLSearchParams(search);
    const connectResult = params.get("connect");
    if (!connectResult) return;

    if (connectResult === "complete") {
      toast({ title: "Payments activated", description: "Your account is ready to accept payments." });
    } else if (connectResult === "pending") {
      toast({ title: "Setup in progress", description: "We're still reviewing your information.", variant: "default" });
    } else if (connectResult === "restricted") {
      toast({ title: "Action required", description: "Additional information is needed to activate your account.", variant: "destructive" });
    } else if (connectResult === "error") {
      toast({ title: "Something went wrong", description: "Could not verify your payment account status.", variant: "destructive" });
    }

    const newSearch = new URLSearchParams(search);
    newSearch.delete("connect");
    const newUrl = "/admin/billing-setup" + (newSearch.toString() ? `?${newSearch}` : "");
    window.history.replaceState(null, "", newUrl);
  }, [search, toast]);

  const status = connectStatus?.status ?? "not_started";

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="mt-0.5 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => setLocation("/admin")}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Payment Setup</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enable online payments so guests and other organizations can pay your organization directly.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <CardTitle className="text-base">Online Payments</CardTitle>
            </div>
            {!isLoading && <StatusBadge status={status} />}
          </div>
          <CardDescription>
            Link a payment account so that payments from guests and partner organizations go directly to you.
            Staio handles the routing automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading status…</p>
          )}

          {!isLoading && status === "not_started" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                You haven't set up a payment account yet. Click below to get started — you'll be guided through
                providing your business information and bank details.
              </p>
              <Button onClick={() => startConnect.mutate()} disabled={startConnect.isPending}>
                <Zap className="w-4 h-4 mr-2" />
                {startConnect.isPending ? "Redirecting…" : "Set up payments"}
              </Button>
            </div>
          )}

          {!isLoading && status === "pending" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Your setup is in progress. If you were interrupted, continue where you left off.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => startConnect.mutate()} disabled={startConnect.isPending}>
                  <Zap className="w-4 h-4 mr-2" />
                  {startConnect.isPending ? "Redirecting…" : "Continue setup"}
                </Button>
                <Button variant="outline" onClick={() => refreshStatus.mutate()} disabled={refreshStatus.isPending}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {refreshStatus.isPending ? "Checking…" : "Check status"}
                </Button>
              </div>
            </div>
          )}

          {!isLoading && status === "restricted" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Additional information is required to activate your payment account.
              </p>
              {connectStatus?.requirementsDue && connectStatus.requirementsDue.length > 0 && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-xs font-medium text-red-800 mb-1">Pending requirements:</p>
                  <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                    {connectStatus.requirementsDue.map((r) => (
                      <li key={r}>{r.replace(/_/g, " ")}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => startConnect.mutate()} disabled={startConnect.isPending}>
                  <Zap className="w-4 h-4 mr-2" />
                  {startConnect.isPending ? "Redirecting…" : "Resume setup"}
                </Button>
                <Button variant="outline" onClick={() => refreshStatus.mutate()} disabled={refreshStatus.isPending}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {refreshStatus.isPending ? "Checking…" : "Check status"}
                </Button>
              </div>
            </div>
          )}

          {!isLoading && status === "complete" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                <span>Your payment account is active. Charges and payouts are enabled.</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const win = window.open("about:blank", "_blank");
                    openStripeDashboard.mutate(win ?? null);
                  }}
                  disabled={openStripeDashboard.isPending}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {openStripeDashboard.isPending ? "Opening…" : "Manage payment account"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => refreshStatus.mutate()} disabled={refreshStatus.isPending}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  {refreshStatus.isPending ? "Checking…" : "Refresh"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={disconnectConnect.isPending}
                  onClick={() => {
                    if (window.confirm("Disconnect your payment account? Booking pages must be deactivated first, and guests won't be able to pay online until you reconnect.")) {
                      disconnectConnect.mutate();
                    }
                  }}
                >
                  <Unlink className="w-3.5 h-3.5 mr-1.5" />
                  {disconnectConnect.isPending ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          )}

          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground">
              Business information provided during setup will be synced to your{" "}
              <button
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => setLocation("/admin/org-settings")}
              >
                Business Profile
              </button>
              {" "}if those fields are currently empty.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
